import React, { useState, useEffect } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { CurrencyField, SelectField, TextField, FieldShell, Section } from "../../../../components/common/formkit/fields";
import { FormSync, FocusFirstError } from "../../../../components/common/formkit/formHelpers";
import AIDescriptionAssist from "../../../../components/common/ai/AIDescriptionAssist";

const MAX_DESCRIPTION_LENGTH = 2000;

const Step2Details = ({ form, setForm, nextStep, formId }) => {
  const loggedInUser = useSelector((state) => state.auth.user);
  const token = loggedInUser?.refreshToken;
  // const token = loggedInUser?.token;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [selectedState, setSelectedState] = useState(form?.state || "");

  // Same page size as the mobile app so the full state/city lists are available
  const postData = {
    page_number: 1,
    page_size: 1000,
    order_by: "ASC",
  };

  // Fetch states
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await axios.post(
          `${baseUrl}/admin/states/1`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStates(response.data.statusType === "SUCCESS" ? response.data.states : []);
      } catch (error) {
        setStates([]);
      }
    };

    fetchStates();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseUrl, token]);

  // Fetch cities when the state changes
  useEffect(() => {
    const fetchCities = async (stateId) => {
      if (!stateId) {
        setCities([]);
        return;
      }
      try {
        const response = await axios.post(
          `${baseUrl}/admin/cities/${stateId}`,
          postData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setCities(response.data.statusType === "SUCCESS" ? response.data.cities : []);
      } catch (error) {
        setCities([]);
      }
    };

    fetchCities(selectedState);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedState, baseUrl, token]);

  const validationSchema = Yup.object({
    price: Yup.number().typeError("Price must be a number").required("Budget is required"),
    name: Yup.string().required("Title is required"),
    state: Yup.string().required("State is required"),
    city: Yup.string().required("City is required"),
    area: Yup.string().required("Area is required"),
    description: Yup.string().max(MAX_DESCRIPTION_LENGTH, "Description too long"),
  });

  const initialValues = {
    price: form?.price ?? "",
    name: form?.name || "",
    state: form?.state || "",
    stateName: form?.stateName || "",
    city: form?.city || "",
    cityName: form?.cityName || "",
    area: form?.area || "",
    description: form?.description || "",
  };

  const handleSubmit = (values, { setSubmitting }) => {
    setForm((prev) => ({
      ...prev,
      ...values,
    }));
    setSubmitting(false);
    if (nextStep) nextStep();
  };

  const toOptions = (list) => (list || []).map((item) => ({ value: item.id, label: item.name }));

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({ setFieldValue, values, errors, touched }) => (
        <Form id={formId} noValidate>
          <FormSync onChange={(v) => setForm((prev) => ({ ...prev, ...v }))} />
          <FocusFirstError />

          <Section title="Listing details" hint="How your property appears to others.">
            <div className="fk-grid">
              <TextField name="name" label="Title" required placeholder="e.g. 3 BHK apartment near metro" />
              <CurrencyField name="price" label="Budget" required placeholder="Enter budget" />
            </div>
          </Section>

          <Section title="Location">
            <div className="fk-grid fk-grid--3">
              <SelectField
                name="state"
                label="State"
                required
                options={toOptions(states)}
                onSelect={(value, option) => {
                  setFieldValue("stateName", option.label);
                  setFieldValue("city", "");
                  setFieldValue("cityName", "");
                  setSelectedState(value);
                }}
              />
              <SelectField
                name="city"
                label="City"
                required
                disabled={!values.state}
                options={toOptions(cities)}
                helper={!values.state ? "Select a state first" : undefined}
                onSelect={(value, option) => setFieldValue("cityName", option.label)}
              />
              <TextField name="area" label="Area" required placeholder="Enter area" />
            </div>
          </Section>

          <Section title="Description">
            <AIDescriptionAssist
              type="property"
              values={{ ...form, ...values }}
              value={values.description}
              onChange={(html) => setFieldValue("description", html)}
              maxLength={MAX_DESCRIPTION_LENGTH}
              format="html"
            >
              <FieldShell
                id="description"
                label="About this property"
                error={touched.description && errors.description}
              >
                <div className="fk-quill" aria-invalid={touched.description && errors.description ? "true" : undefined}>
                  <ReactQuill
                    theme="snow"
                    value={values.description}
                    onChange={(value) => setFieldValue("description", value)}
                  />
                </div>
                <div className={`fk-counter ${values.description.length > MAX_DESCRIPTION_LENGTH ? "fk-counter--over" : ""}`}>
                  <span>
                    {values.description.length}/{MAX_DESCRIPTION_LENGTH} characters
                  </span>
                  {values.description.length > MAX_DESCRIPTION_LENGTH && <span>Description exceeds the maximum length.</span>}
                </div>
              </FieldShell>
            </AIDescriptionAssist>
          </Section>
        </Form>
      )}
    </Formik>
  );
};

export default Step2Details;
